import { useState } from 'react';
import { Copy, Check, LifeBuoy } from 'lucide-react';

import { Button } from '../ui/Button';
import { UserIdentity } from './UserIdentity';
import { useAppSelector } from '../app/hooks';
import { selectUser } from '../store/selectors/auth';
import { SUPPORT_EMAIL, supportMailto } from '../lib/support';
import { APP_VERSION, APP_BUILD_ID, APP_BUILD_TIME } from '../lib/appVersion';
import {
  Dialog,
  DialogTitle,
  DialogFooter,
  DialogHeader,
  DialogContent,
  DialogDescription,
} from '../ui/Dialog';

const Row = ({ label, value }) => (
  <div className="flex items-baseline justify-between gap-4 border-b border-border py-2 last:border-b-0">
    <span className="text-sm text-gray-dark">{label}</span>
    <span className="truncate font-mono text-sm text-foreground">{value}</span>
  </div>
);

export const AboutDialog = ({ open, onOpenChange }) => {
  const user = useAppSelector(selectUser);
  const [copied, setCopied] = useState(false);

  const buildTime = new Date(APP_BUILD_TIME).toLocaleString();
  // Everything a report needs, in the order someone would read it out loud.
  const details = [
    `Version: ${APP_VERSION}`,
    `Build: ${APP_BUILD_ID}`,
    `Built: ${buildTime}`,
    `User: ${user?.user_id ?? 'signed out'}`,
    `URL: ${window.location.href}`,
  ].join('\n');

  const handleCopy = async () => {
    await navigator.clipboard.writeText(details);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>About this admin panel</DialogTitle>
          <DialogDescription>
            Quote these when you report a problem so we can pin down the exact build.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col">
          <Row label="Version" value={APP_VERSION} />
          <Row label="Build" value={APP_BUILD_ID} />
          <Row label="Built" value={buildTime} />
          <Row
            label="Signed in as"
            value={
              user ? (
                <UserIdentity
                  id={user.user_id}
                  name={user.name}
                  email={user.email}
                  className="items-end font-sans"
                />
              ) : (
                'signed out'
              )
            }
          />
        </div>
        <p className="text-sm text-gray-dark">
          Technical difficulties? Write to{' '}
          <a
            href={supportMailto(`Dusq Admin ${APP_VERSION} (${APP_BUILD_ID})`)}
            className="text-foreground underline underline-offset-4 hover:text-primary"
          >
            {SUPPORT_EMAIL}
          </a>
          .
        </p>
        <DialogFooter>
          <Button variant="outlined" onClick={handleCopy} prefix={copied ? <Check /> : <Copy />}>
            {copied ? 'Copied' : 'Copy details'}
          </Button>
          <Button variant="primary" asChild>
            <a href={supportMailto(`Dusq Admin ${APP_VERSION} (${APP_BUILD_ID})`)}>
              <LifeBuoy className="h-4 w-4" />
              Email support
            </a>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
